import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BriefcaseBusiness, GraduationCap, Sparkles, Trophy } from 'lucide-react';
import useInView from '../hooks/useInView';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.1);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const pathwayCards = [
  {
    icon: Sparkles,
    title: 'Start with purpose',
    text: 'Children begin inside ROMIND KIDS through clubs, challenges, creativity, kindness and safe communication.',
  },
  {
    icon: Trophy,
    title: 'Build a visible record',
    text: 'Year after year, effort can become achievements, portfolio evidence, Academy progress and leadership signals.',
  },
  {
    icon: GraduationCap,
    title: 'Compete for scholarship',
    text: 'The best of the best may qualify for ROMIND Scholarship funding toward IT-focused study at a leading university.',
  },
  {
    icon: BriefcaseBusiness,
    title: 'Return as talent',
    text: 'Graduates can grow into internships, paid roles and long-term work inside the ROMIND ecosystem.',
  },
];

const AcademyTeaserSection = () => (
  <section data-testid="academy-teaser-section" className="relative py-24 bg-[#080714] overflow-hidden">
    <div className="absolute top-0 left-1/4 w-80 h-80 bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" />
    <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-cyan-600/10 rounded-full blur-[110px] pointer-events-none" />
    <div
      className="absolute inset-0 pointer-events-none opacity-35"
      style={{
        background:
          'radial-gradient(circle at 18% 20%, rgba(157,76,221,0.16), transparent 28%), radial-gradient(circle at 82% 18%, rgba(0,240,255,0.12), transparent 26%), radial-gradient(circle at 50% 90%, rgba(255,0,127,0.11), transparent 32%)',
      }}
    />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
        <FadeIn className="lg:col-span-6">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-300 mb-3 font-outfit">
            ROMIND Academy
          </p>
          <h2 className="font-outfit font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.04] tracking-tight mb-5">
            What if screen time became a path to a
            <span className="gradient-text"> world-class future?</span>
          </h2>
          <p className="text-white/58 text-base sm:text-lg leading-relaxed max-w-2xl mb-6">
            ROMIND KIDS is not only about protecting children online. It is designed to help parents turn digital time into a long-term growth path: creativity, discipline, teamwork, IT thinking, portfolio building and serious future opportunities.
          </p>
          <p className="text-white/50 text-sm sm:text-base leading-relaxed max-w-2xl mb-8">
            For selected best-of-the-best Academy graduates who meet strict academic and ecosystem criteria, the ROMIND Scholarship Fund is designed to support IT-focused university education at an approved leading university anywhere in the world — followed by internships, career development and long-term work inside the ROMIND ecosystem.
          </p>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/academy"
              data-testid="academy-teaser-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-purple-600/80 border border-purple-400/45 text-white hover:bg-purple-500 hover:shadow-[0_0_28px_rgba(157,76,221,0.24)] transition-all duration-200"
            >
              Read the Academy Pathway <ArrowRight size={14} />
            </Link>
            <span className="inline-flex items-center px-6 py-3 rounded-full font-semibold text-sm bg-white/5 border border-white/12 text-white/55 select-none">
              Safe today. Skilled tomorrow.
            </span>
          </div>
        </FadeIn>

        <FadeIn delay={120} className="lg:col-span-6">
          <div className="glass-card p-5 sm:p-6 lg:p-7 glow-border">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <p className="text-xs font-outfit font-semibold text-white/40 uppercase tracking-widest mb-2">
                  A Future Pathway
                </p>
                <h3 className="font-outfit font-bold text-2xl sm:text-3xl text-white tracking-tight">
                  A small doorway can lead to serious opportunity.
                </h3>
              </div>
              <div className="hidden sm:flex w-12 h-12 rounded-2xl bg-white/5 border border-white/10 items-center justify-center flex-shrink-0">
                <GraduationCap size={22} className="text-cyan-300" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {pathwayCards.map((card, index) => (
                <div
                  key={card.title}
                  data-testid={`academy-pathway-card-${index}`}
                  className="rounded-2xl bg-white/[0.035] border border-white/10 p-4 transition-all duration-200 hover:border-cyan-400/25 hover:bg-white/[0.055]"
                >
                  <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-3">
                    <card.icon size={17} className="text-purple-300" />
                  </div>
                  <h4 className="font-outfit font-semibold text-white text-sm mb-1.5">{card.title}</h4>
                  <p className="text-white/43 text-xs leading-relaxed">{card.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-2xl bg-[#05050A]/55 border border-white/10 p-4">
              <p className="text-white/58 text-sm leading-relaxed">
                The promise is serious, not automatic: effort must be real, progress must be visible, school performance must matter, and selection must be highly competitive. For parents, the point is simple — ROMIND grows with your child, and the journey can open doors that family income alone should not decide.
              </p>
            </div>
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

export default AcademyTeaserSection;
